import React from 'react';
import { Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const PricingCard = ({ 
  name, 
  price, 
  duration_days, 
  features, 
  is_popular,
  index,
  id
}) => {
  const navigate = useNavigate();

  const handleSelect = () => {
    navigate('/payment', {
      state: {
        packageDetails: {
          name,
          price,
          duration_days,
          features,
          is_popular,
          id
        }
      }
    });
  };

  // Define the order of features to display
  const featureOrder = [
    'max_leads',
    'max_deals',
    'sms_credits',
    'max_offers',
    'max_sender_ids',
    'max_promotions',
    'number_of_users'
  ];

  // Sort features according to the defined order
  const sortedFeatures = featureOrder
    .filter(key => key in features)
    .map(key => ({ key, value: features[key] }));

  // Helper function to format feature text
  const formatFeatureText = (key, value) => {
    switch (key) {
      case 'max_leads':
        return `${value.toLocaleString()} Leads`;
      case 'max_deals':
        return `${value} Deal${value !== 1 ? 's' : ''}`;
      case 'sms_credits':
        return `${value.toLocaleString()} SMS Credits`;
      case 'max_offers':
        return `${value} Offer${value !== 1 ? 's' : ''}`;
      case 'max_sender_ids':
        return `${value} Sender ID${value !== 1 ? 's' : ''}`;
      case 'max_promotions':
        return `${value} Promotion${value !== 1 ? 's' : ''}`;
      case 'number_of_users':
        return `${value} User${value !== 1 ? 's' : ''}`;
      default:
        return value;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        duration: 0.5,
        delay: index * 0.2,
        ease: "easeOut"
      }}
      whileHover={{ scale: 1.05 }}
      className={`
        relative w-full max-w-md rounded-2xl p-6
        ${is_popular ? 'border-2 border-[#FF6C6C]' : 'border border-gray-200'}
        bg-white shadow-lg min-w-[300px]
      `}
    >
      {is_popular && (
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: (index * 0.2) + 0.3 }}
          className="absolute -top-4 right-8 bg-[#FF6C6C] text-white px-4 py-1 rounded-full text-sm font-medium"
        >
          Most Popular
        </motion.div>
      )}
      
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900">{name}</h3>
        <div className="mt-4">
          <span className="text-2xl font-bold text-gray-900">
            {price === 0 ? 'Free' : `GH₵${price.toFixed(2)}`}
          </span>
          {price !== 0 && (
            <span className="text-gray-500">/{duration_days === 30 ? 'month' : `${duration_days} days`}</span>
          )}
        </div>
      </div>

      <ul className="mt-8 space-y-4">
        {sortedFeatures.map(({ key, value }, i) => (
          <motion.li
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ 
              delay: (index * 0.2) + (i * 0.1),
              duration: 0.3
            }}
            key={key}
            className="flex items-center space-x-3"
          >
            <Check className={`h-5 w-5 ${
              key === 'analytics_access' && !value 
                ? 'text-[#FF6C6C]' 
                : 'text-[#FF6C6C]'
            }`} />
            <span className={`${
              key === 'analytics_access' && !value 
                ? 'text-gray-600' 
                : 'text-gray-600'
            }`}>
              {formatFeatureText(key, value)}
            </span>
          </motion.li>
        ))}
      </ul>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleSelect}
        className={`
          mt-8 w-full rounded-lg py-3 px-6 text-center font-semibold
          transition-all duration-200
          ${is_popular 
            ? 'bg-[#FF6C6C] text-white hover:bg-[#ff5555]' 
            : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
          }
        `}
      >
        {price === 0 ? 'Start Free Trial' : 'Get Started'}
      </motion.button>
    </motion.div>
  );
};

export default PricingCard;
